"use client";
import React from "react";
import { useState, useEffect, useRef } from "react";
import { SelectProps } from "src/app/components/ui/Form/Select/types";

import { useOutsideClick } from "../../../hooks/useOutsideClick";
import { TiTimes } from "react-icons/ti";

const Select: React.FC<SelectProps> = ({
  label,
  required = false,
  placeholder,
  inputW,
  noBorder,
  options,
  value = "",
  onChange,
  borderStyle,
  dropdownItemStyle,
  onClick,
  clickable = false,
  dropdownStyle,
  cancelStyle,
  inputParentStyle,
  className,
  readonly,
  name,
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedValue, setSelectedValue] = useState(value);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync external value with local state
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // Close dropdown when clicked outside
  useOutsideClick(dropdownRef, () => setDropdownOpen(false));

  // Filter options based on search term
  const filteredOptions = options?.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle selecting an option
  const handleSelect = (option: string) => {
    setSelectedValue(option);
    setSearchTerm(""); // Clear search after selection
    setDropdownOpen(false);
    onChange?.(option); // Notify parent with the selected option
  };

  // Handle search input changes (no parent notification)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    setSelectedValue(""); // Clear selection to enable search
    setDropdownOpen(true); // Ensure dropdown opens during typing
  };

  // Clear selected value or reset search term on cancel
  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    setSearchTerm(""); // Reset search term
    setSelectedValue(""); // Reset selected value
    onChange?.(""); // Notify parent that no value is selected
    setDropdownOpen(false); // Close dropdown after clearing
  };

  // Toggle dropdown open state based on the arrow clicked
  const toggleDropdown = (direction: "up" | "down") => {
    if (direction === "down") {
      setDropdownOpen(true); // Open dropdown on arrow down click
    } else {
      setDropdownOpen(false); // Close dropdown on arrow up click
    }
  };

  // Open dropdown when input is focused and no value is selected
  const handleFocus = () => {
    if (!selectedValue && !searchTerm) {
      setDropdownOpen(true); // Open dropdown when input is focused and no value is selected
    }
  };

  return (
    <div
      className={`relative ${
        readonly && "pointer-events-none"
      } h-full ${className}`}
      ref={dropdownRef}
    >
      <label className="block ">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className={`relative h-full`}>
        <div
          className={`flex items-center h-full shadow text-sm appearance-none ${
            !noBorder ? "bg-label-main border-themeColor " : "border-none"
          } border  rounded-md bg-white  text-secondary-main shadow-none !pl-0 font-medium p-3 leading-tight   focus:outline-none ${inputParentStyle}  ${borderStyle}`}
          tabIndex={0}
          aria-expanded={isDropdownOpen}
        >
          <input
            onClick={() => {
              if (onClick) {
                onClick();
              }
            }}
            ref={inputRef}
            name={`${name}-no-autofill`} // change name so browser has no history
            type="text"
            autoComplete="new-value" // fake autocomplete value
            style={{
              width: inputW,
            }}
            placeholder={placeholder}
            className="placeholder:text-sm w-full inline-block h-full p-1 px-3 placeholder:font-normal placeholder:text-disabled-main outline-none"
            value={searchTerm || selectedValue}
            onChange={handleInputChange}
            onFocus={(e) => {
              if (handleFocus) handleFocus();
              e.target.removeAttribute("readonly"); // optional trick
            }}
            readOnly={!!selectedValue || clickable}
            data-form-type="other" // Safari autofill prevention
          />

          <div className="items">
            {selectedValue ? (
              <button
                onClick={(e) => {
                  if (onClick) {
                    onClick();
                  }
                  handleClear(e);
                }}
                type="button"
                className={` text-white flex items-center justify-center bg-black border-label-main  size-4 rounded-full`}
              >
                <TiTimes />
              </button>
            ) : (
              <button
                type="button"
                className=""
                onClick={() => {
                  if (onClick) {
                    onClick();
                  }
                  toggleDropdown(isDropdownOpen ? "up" : "down");
                }}
              >
                <span
                  className={`transition-all block transform duration-500 ${
                    !isDropdownOpen ? "rotate-0" : "rotate-[180deg]"
                  }`}
                >
                  {cancelStyle ? <ArrowDownVariant /> : <ArrowDown />}
                </span>
              </button>
            )}
          </div>
        </div>
        {isDropdownOpen && (
          <ul
            className={`absolute z-[20] scrollbar-hide bg-white border rounded shadow-lg max-h-48 w-[200px] sm:!left-0 right-0 overflow-auto mt-1 ${dropdownStyle}`}
          >
            {filteredOptions?.length > 0 ? (
              filteredOptions.map((option, index) => (
                <li
                  key={index}
                  className={`px-4 text-sm py-2 hover:bg-gray-200 capitalize cursor-pointer ${dropdownItemStyle}`}
                  onClick={() => handleSelect(option)} // Notify parent on item select
                >
                  {option}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500">No matches found</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Select;

export const ArrowDown = () => {
  return (
    <svg
      width="16"
      height="13"
      viewBox="0 0 16 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.0946 11.6675C7.48728 12.287 8.39116 12.287 8.78384 11.6675L15.206 1.53536C15.6281 0.869559 15.1497 0 14.3614 0H1.51702C0.728741 0 0.250385 0.869559 0.672401 1.53536L7.0946 11.6675Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};

export const ArrowDownVariant = () => {
  return (
    <svg
      width="14"
      height="9"
      viewBox="0 0 14 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1286_218191)">
        <path
          d="M2.15122 1.57702C2.09553 1.52439 2.03002 1.48325 1.95843 1.45594C1.88683 1.42863 1.81056 1.41569 1.73397 1.41786C1.65738 1.42002 1.58196 1.43725 1.51203 1.46857C1.4421 1.49988 1.37901 1.54466 1.32639 1.60035C1.27376 1.65604 1.23262 1.72155 1.20531 1.79315C1.178 1.86474 1.16506 1.94101 1.16723 2.0176C1.16939 2.09419 1.18662 2.16961 1.21794 2.23954C1.24925 2.30948 1.29403 2.37256 1.34972 2.42519L6.59972 7.38352C6.70803 7.48592 6.85142 7.54297 7.00047 7.54297C7.14952 7.54297 7.29291 7.48592 7.40122 7.38352L12.6518 2.42519C12.7087 2.3729 12.7547 2.30984 12.787 2.23965C12.8193 2.16946 12.8374 2.09354 12.8401 2.01631C12.8429 1.93908 12.8303 1.86208 12.803 1.78977C12.7757 1.71746 12.7344 1.65129 12.6813 1.5951C12.6282 1.53892 12.5645 1.49383 12.4939 1.46247C12.4233 1.4311 12.3471 1.41409 12.2699 1.4124C12.1926 1.41072 12.1158 1.42441 12.0439 1.45267C11.9719 1.48093 11.9063 1.52319 11.8509 1.57702L7.00047 6.15735L2.15122 1.57702Z"
          fill="var(--primary-color)"
        />
      </g>
      <defs>
        <clipPath id="clip0_1286_218191">
          <rect
            width="8.75"
            height="14"
            fill="white"
            transform="matrix(0 -1 1 0 0 9)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};
